import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { workspacesService, type WorkspaceWithRole } from "@/services/workspacesService";

const STORAGE_KEY_CURRENT_WORKSPACE = "currentWorkspaceId";

export type WorkspaceContextType = {
  /** null = personal account; string = workspace id */
  currentWorkspaceId: string | null;
  setCurrentWorkspaceId: (id: string | null) => void;
  /** List of workspaces the user belongs to (for switcher) */
  workspaces: WorkspaceWithRole[];
  isLoadingWorkspaces: boolean;
  refetchWorkspaces: () => void;
  /** Convenience: true when in workspace context (not personal) */
  isWorkspaceContext: boolean;
};

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [currentWorkspaceId, setCurrentWorkspaceIdState] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(STORAGE_KEY_CURRENT_WORKSPACE) || null;
  });

  const setCurrentWorkspaceId = useCallback((id: string | null) => {
    setCurrentWorkspaceIdState(id);
    if (typeof window !== "undefined") {
      if (id) localStorage.setItem(STORAGE_KEY_CURRENT_WORKSPACE, id);
      else localStorage.removeItem(STORAGE_KEY_CURRENT_WORKSPACE);
    }
  }, []);

  const {
    data: workspaces = [],
    isLoading: isLoadingWorkspaces,
    refetch: refetchWorkspaces,
  } = useQuery({
    queryKey: ["workspaces", user?.id],
    queryFn: () => workspacesService.getWorkspaces(),
    enabled: !!user,
  });

  // If current workspace id is set but user is no longer a member, clear it
  useEffect(() => {
    if (!currentWorkspaceId || !workspaces.length) return;
    const stillMember = workspaces.some((w) => w.id === currentWorkspaceId);
    if (!stillMember) setCurrentWorkspaceId(null);
  }, [currentWorkspaceId, workspaces, setCurrentWorkspaceId]);

  const value: WorkspaceContextType = {
    currentWorkspaceId,
    setCurrentWorkspaceId,
    workspaces,
    isLoadingWorkspaces,
    refetchWorkspaces,
    isWorkspaceContext: currentWorkspaceId !== null,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace(): WorkspaceContextType {
  const ctx = useContext(WorkspaceContext);
  if (ctx === undefined) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return ctx;
}
